/**
 * Boli Live - Admin Guard Component
 *
 * Protects admin routes that require admin or moderator role.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../lib/stores/auth-store';
import { Loader2 } from 'lucide-react';

interface AdminGuardProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'moderator';
}

export function AdminGuard({ children, requiredRole = 'moderator' }: AdminGuardProps) {
  const { isAuthenticated, isInitialized, isAdmin, isModerator } = useAuthStore();
  const location = useLocation();

  // Show loading while checking auth
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to profile/login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/profile" state={{ from: location }} replace />;
  }

  // Check role-based access
  const hasAccess = requiredRole === 'admin'
    ? isAdmin
    : (isAdmin || isModerator);

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="text-6xl mb-4">403</div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
        <p className="text-muted-foreground text-center mb-6">
          You don't have permission to access this page.
          {requiredRole === 'admin' ? ' Admin access is required.' : ' Moderator access is required.'}
        </p>
        <a
          href="/"
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          Go Home
        </a>
      </div>
    );
  }

  return <>{children}</>;
}
